import { Routes } from "@angular/router";
import { AuthGuard } from "@core/guards/auth.guard";
import { FeatureFlagGuard } from "@core/guards/feature-flag.guard";
import { CategoriesResolver } from "@core/resolvers/categories.resolver";
import { AuthStateResolver } from "@core/resolvers/auth-state.resolver";
import { LegalDocumentResolver } from "@core/resolvers/legal-document.resolver";
import { CUSTOMER_FEATURES } from "@ophardt/shopware-api";

export const routes: Routes = [
  {
    path: "page-not-found",
    loadComponent: () => import("@pages/not-found-page/not-found-page.component").then((c) => c.NotFoundPageComponent),
  },
  {
    path: "login",
    loadComponent: () => import("@pages/login-page/login-page.component").then((c) => c.LoginPageComponent),
  },
  {
    path: "reset-password",
    loadComponent: () => import("@pages/login-page/login-page.component").then((c) => c.LoginPageComponent),
    data: {
      isPasswordRecovery: true,
    },
  },
  {
    path: "set-password",
    loadComponent: () => import("@pages/login-page/login-page.component").then((c) => c.LoginPageComponent),
    data: {
      isSetNewCustomerPassword: true,
    },
  },
  {
    path: "register",
    loadComponent: () => import("@pages/register-page/register-page.component").then((c) => c.RegisterPageComponent),
  },
  {
    path: "legal",
    loadComponent: () => import("@pages/legal-page/legal-page.component").then((c) => c.LegalPageComponent),
    resolve: {
      legalDocument: LegalDocumentResolver,
    },
    runGuardsAndResolvers: "paramsOrQueryParamsChange",
  },
  {
    path: "newsletter-subscription-success",
    loadComponent: () =>
      import("@pages/newsletter-subscription-success-page/newsletter-subscription-success-page.component").then(
        (c) => c.NewsletterSubscriptionSuccessPageComponent,
      ),
  },
  {
    path: "registration-success",
    loadComponent: () => import("@pages/registration-success-page/registration-success-page.component").then((c) => c.RegistrationSuccessPageComponent),
  },
  {
    path: "service",
    children: [
      {
        path: "faq",
        loadComponent: () => import("@pages/service-faq-page/service-faq-page.component").then((c) => c.ServiceFaqPageComponent),
      },
      {
        path: "newsletter",
        loadComponent: () => import("@pages/service-newsletter-page/service-newsletter-page.component").then((c) => c.ServiceNewsletterPageComponent),
      },
      {
        path: "**",
        redirectTo: "faq",
      },
    ],
  },
  {
    path: "",
    canActivate: [AuthGuard],
    resolve: {
      authState: AuthStateResolver,
    },
    children: [
      {
        path: "home",
        loadComponent: () => import("@pages/home-page/home-page.component").then((c) => c.HomePageComponent),
      },
      {
        path: "products",
        data: { isShowCategoryButton: false },
        loadComponent: () => import("@pages/products-page/products-page.component").then((c) => c.ProductsPageComponent),
        resolve: {
          categories: CategoriesResolver,
        },
      },
      {
        path: "product/:id",
        loadComponent: () => import("@pages/product-details-page/product-details-page.component").then((c) => c.ProductDetailsPageComponent),
      },
      {
        path: "cart",
        loadComponent: () => import("@pages/cart-page/cart-page.component").then((c) => c.CartPageComponent),
      },
      {
        path: "checkout",
        loadComponent: () => import("@pages/checkout-page/checkout-page.component").then((c) => c.CheckoutPageComponent),
      },
      {
        path: "checkout/success",
        loadComponent: () => import("@pages/checkout-success-page/checkout-success-page.component").then((c) => c.CheckoutSuccessPageComponent),
      },
      {
        path: "checkout/quote/:id",
        loadComponent: () => import("@pages/checkout-quote-page/checkout-quote-page.component").then((c) => c.CheckoutQuotePageComponent),
      },
      {
        path: "account",
        loadComponent: () => import("@pages/account-page/account-page.component").then((c) => c.AccountPageComponent),
        children: [
          {
            path: "overview",
            loadComponent: () => import("./features/account/components/account-overview/account-overview.component").then((c) => c.AccountOverviewComponent),
          },
          {
            path: "profile",
            loadComponent: () => import("./features/account/components/account-profile/account-profile.component").then((c) => c.AccountProfileComponent),
          },
          {
            path: "addresses",
            loadComponent: () => import("./features/account/components/account-addresses/account-addresses.component").then((c) => c.AccountAddressesComponent),
          },
          {
            path: "payment-methods",
            loadComponent: () =>
              import("./features/account/components/account-payment-methods/account-payment-methods.component").then((c) => c.AccountPaymentMethodsComponent),
          },
          {
            path: "orders",
            loadComponent: () => import("./features/account/components/account-orders/account-orders.component").then((c) => c.AccountOrdersComponent),
            canActivate: [FeatureFlagGuard],
          },
          {
            path: "orders/:id",
            loadComponent: () =>
              import("./features/account/components/account-order-details/account-order-details.component").then((c) => c.AccountOrderDetailsComponent),
            canActivate: [FeatureFlagGuard],
          },
          {
            path: "quotes",
            loadComponent: () => import("./features/account/components/account-quotes/account-quotes.component").then((c) => c.AccountQuotesComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.QUOTE_MANAGEMENT },
          },
          {
            path: "quotes/details/:id",
            loadComponent: () =>
              import("./features/account/components/account-quote-details/account-quote-details.component").then((c) => c.AccountQuoteDetailsComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.QUOTE_MANAGEMENT },
          },
          {
            path: "employees",
            loadComponent: () => import("./features/account/components/account-employees/account-employees.component").then((c) => c.AccountEmployeesComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.EMPLOYEE_MANAGEMENT },
          },
          {
            path: "roles",
            loadComponent: () => import("./features/account/components/account-roles/account-roles.component").then((c) => c.AccountRolesComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.EMPLOYEE_MANAGEMENT },
          },
          {
            path: "roles/create",
            loadComponent: () =>
              import("./features/account/components/account-create-role/account-create-role.component").then((c) => c.AccountCreateRoleComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.EMPLOYEE_MANAGEMENT },
          },
          {
            path: "roles/edit/:id",
            loadComponent: () => import("./features/account/components/account-edit-role/account-edit-role.component").then((c) => c.AccountEditRoleComponent),
            canActivate: [FeatureFlagGuard],
            data: { featureFlag: CUSTOMER_FEATURES.EMPLOYEE_MANAGEMENT },
          },
          {
            path: "",
            redirectTo: "overview",
            pathMatch: "full",
          },
        ],
      },
      {
        path: "compare",
        loadComponent: () => import("@pages/compare-page/compare-page.component").then((c) => c.ComparePageComponent),
      },
      {
        path: "favorite-products",
        loadComponent: () => import("@pages/favorite-products-page/favorite-products-page.component").then((c) => c.FavoriteProductsPageComponent),
      },
      {
        path: "",
        redirectTo: "home",
        pathMatch: "full",
      },
    ],
  },
  {
    path: "**",
    redirectTo: "page-not-found",
  },
];
